import React from "react";
import { XIcon } from "lucide-react";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import { useBreakpoints, useTranslate } from "@/hooks";
import { Button } from "@/components/main";
import { cn } from "@/helpers/tailwind";
import isNil from "lodash.isnil";

interface BuyDialogProps {
  open: boolean;
  artistInfo?: {
    name: string;
    image: string;
    percentage: number; // 0-1 for 0-100%
  };
  content: React.ReactNode;
  buyButton: React.ReactNode;
  tosQuickAgreement?: React.ReactNode;
  setOpen: (open: boolean) => void;
}

export const BuyDialog: React.FC<BuyDialogProps> = ({
  open,
  artistInfo,
  content,
  buyButton,
  tosQuickAgreement,
  setOpen,
}) => {
  const t = useTranslate();
  const { isGreaterThan } = useBreakpoints();

  if (isNil(artistInfo) || isGreaterThan("lg")) {
    return null;
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent
        overlayClassName="bg-slate-900/80"
        className="rounded-t-[30px] border-none bg-slate-800 px-8 py-6">
        <DrawerHeader className="px-0 pt-1">
          <DrawerClose className="absolute right-7 top-6 rounded-sm">
            <XIcon className="size-5" />
          </DrawerClose>
          <div className="mb-4 flex items-center">
            <DrawerTitle>Buy</DrawerTitle>
          </div>
          {/* not visible - only set for a11y */}
          <DrawerDescription className="mt-3 hidden font-geist font-light">
            You're about to support{" "}
            <span className="font-bold">{artistInfo.name}</span>. Are you sure
            you want to proceed?
          </DrawerDescription>
        </DrawerHeader>

        <div>
          <div
            className={cn(
              "flex items-center justify-between gap-2",
              "mb-4 rounded-lg bg-slate-900 p-1 pr-2"
            )}>
            <img
              src={artistInfo.image}
              alt={artistInfo.name}
              className="size-9 rounded-lg"
            />
            <p className="flex-grow">{artistInfo.name}</p>
            <p>{artistInfo.percentage.toFixed(2)}%</p>
          </div>
          {content}
        </div>

        <DrawerFooter className="mt-4 flex-row justify-between px-0 [&>*]:basis-1/2">
          <DrawerClose asChild>
            <Button
              variant="outline"
              className="border-slate-200 bg-transparent hover:bg-transparent">
              {t("common.other.cancel")}
            </Button>
          </DrawerClose>
          {buyButton}
        </DrawerFooter>
        {tosQuickAgreement && <div className="mt-2">{tosQuickAgreement}</div>}
      </DrawerContent>
    </Drawer>
  );
};
